// Safe localStorage wrapper. Never throws: quota errors and private-mode
// failures degrade to an in-memory map so the app keeps working (trend history
// just won't persist across reloads).

export const PREFIX = 'election-tracker:';

const memory = new Map<string, string>();
let persistent = true;

function tryLocal<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    persistent = false;
    return fallback;
  }
}

export function storageIsPersistent(): boolean {
  return persistent;
}

export function readRaw(key: string): string | null {
  const k = PREFIX + key;
  if (memory.has(k)) return memory.get(k) ?? null;
  return tryLocal(() => window.localStorage.getItem(k), null);
}

export function writeRaw(key: string, value: string): boolean {
  const k = PREFIX + key;
  memory.set(k, value);
  return tryLocal(() => {
    window.localStorage.setItem(k, value);
    return true;
  }, false);
}

export function removeRaw(key: string): void {
  const k = PREFIX + key;
  memory.delete(k);
  tryLocal(() => window.localStorage.removeItem(k), undefined);
}

export function readJSON<T>(key: string, fallback: T): T {
  const raw = readRaw(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Persist JSON. On quota errors, `onQuota` may shrink the value; we retry once. */
export function writeJSON<T>(key: string, value: T, onQuota?: (v: T) => T): boolean {
  try {
    const ok = writeRaw(key, JSON.stringify(value));
    if (ok) return true;
  } catch {
    /* fall through to quota handling */
  }
  if (onQuota) {
    try {
      return writeRaw(key, JSON.stringify(onQuota(value)));
    } catch {
      return false;
    }
  }
  return false;
}
